import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Event, NavigationEnd, NavigationStart, Router, RoutesRecognized } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { filter, map, mergeMap, pairwise } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { commonVar } from './core/data/constants';
import { RootScopeDeclare } from './core/rootscope-declare';
import { AmplitudeAnalyticsService } from './core/services/amplitude-analytics.service';
import { AuthenticationService } from './core/services/authentication.service';
import { FacilityService } from './core/services/facility.service';
import { FeatureFlagsService } from './core/shared-service/feature-flags.service';
import { RootScopeService } from './core/shared-service/root-scope.service';
import { CommonUtil } from './core/Utils/common-util';
import { BreadcrumbService } from './post-login/services/breadcrumb.service';
import { MultiFacilityService } from './post-login/services/multi-facility.service';
import { PageTitleService } from './post-login/services/page-title.service';
import { UserLogoutService } from './post-login/services/user-logout.service';
import { VideoLibraryService } from './post-login/video-library/video-library.service';
import { SsoSyncPasswordService } from './pre-login/services/sso-sync-password.service';
import { SsoUserAuthenticationService } from './pre-login/services/sso-user-authentication.service';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit {
  // basePath="http://localhost:8443/lfcw/";
  public currentHostName: string;
  public oamError = false;
  public isSSoError = false;
  public ssoCookie: string;
  public userValidations: any;
  public staySigned = true;
  isCommonPath = false;
  currentPath: string;
  currentPathCss: string;
  rootScopeData: RootScopeDeclare;
  _routeOnChange: Subscription;

  @HostListener('window:unload', ['$event'])
  unloadHandler(event: any) {
    event.preventDefault();
  }
  constructor(
    private router: Router,
    private commonUtil: CommonUtil,
    private syncSsoPassword: SsoSyncPasswordService,
    private authenticationService: AuthenticationService,
    private authenticateSsoUsers: SsoUserAuthenticationService,
    private cookieService: CookieService,
    private activatedRoute: ActivatedRoute,
    private rootScopeService: RootScopeService,
    private pageTitleService: PageTitleService,
    private breadcrumbService: BreadcrumbService,
    private featureService: FeatureFlagsService,
    private _amplitudeService: AmplitudeAnalyticsService,
    private _vdoLibraryService: VideoLibraryService,
    private _facilityService: FacilityService,
    private multiFacService: MultiFacilityService,
    private userLogoutService: UserLogoutService
  ) {
    this.getRootScopeData();

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        // If Route is not the path of fcHypbridRoute, then redirect to fcConnectHostPath.
        // routeRedirectPath would be overwrite with download software path if the requested url is software download.
        if (this.isDownloadSoftwareRoute(event.url)) {
          window.location.replace(environment.fcConnectDownloadSoftwarePath);
        }

        // Show loading indicator
        this.rootScopeData.currentUrl = event.url;
      }

      if (event instanceof NavigationEnd) {
        this.currentPath = this.router.url.split('?')[0];
        const previousFlag = this.isCommonPath;
        if (commonVar.paths.includes(this.currentPath)) {
          this.isCommonPath = true;
        } else {
          this.isCommonPath = false;
          if (previousFlag) {
            setTimeout(() => {
              this.authenticationService.authenticateUser();
              this.initializeLoginChecks();
            });
          }
        }
      }
    });

    this.router.events
      .pipe(
        filter((evt: any) => evt instanceof RoutesRecognized),
        pairwise()
      )
      .subscribe((events: RoutesRecognized[]) => {
        this.rootScopeData.previousUrl = events[0].urlAfterRedirects;
        this.rootScopeData.currentUrl = events[1].urlAfterRedirects;
        this.rootScopeService.updateRootScope(this.rootScopeData);
      });

    this._routeOnChange = this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
        map((data) => data?.meta)
      )
      .subscribe((event) => {
        this.pageTitleService.setTitle(event?.title ? event.title : '');
        this.pageTitleService.setRedirectBackPath(event?.redirectBackPath ? event.redirectBackPath : '');
        this.breadcrumbService.set(event?.breadcrumb ? event.breadcrumb : []);
        if (event?.featureName) {
          this._amplitudeService.pageName = event?.featureName;
          this._amplitudeService.logEvent({ elementName: 'Page', action: 'Loaded' });
        }
      });
    window.addEventListener('beforeunload', (event) => {
      const fcHybridLegacHalo = this.featureService.isOn('facility-connect-hybrid-mode');
      if (fcHybridLegacHalo) {
        this.userLogoutService.userLogout().subscribe();
        this.clearStorageAndResetSetting();
        console.log('impersonation session expired successfuly loggedOut.');
      }
      return event;
    });
  }

  clearStorageAndResetSetting() {
    this.afterLogout();
    this.authenticationService.signoutReset();
    this.multiFacService.resetMultiFacilitiesList();
    this._facilityService.reset();
  }

  afterLogout() {
    this.clearStorageAndCookies();
    this.rootScopeData.isAuthenticated = false;
  }

  clearStorageAndCookies(): void {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('vtTokenKey');
    localStorage.removeItem('vtUserType');
    localStorage.removeItem('vtManagerType');
    localStorage.removeItem('ssoFederated');
    localStorage.removeItem('newEquipmentAdded');
    localStorage.removeItem('isSsoFederated');
    localStorage.removeItem('lefNavigationExpandStatus');

    sessionStorage.removeItem('vtTokenKey');
    sessionStorage.removeItem('hideSurveyPopup');
    sessionStorage.removeItem('hideOutdatedOnboardingPopup');

    this.cookieService.delete('vtTokenKey');
    this.cookieService.delete('ssoCookie');
    this.cookieService.delete('ssoFederated');
    this.cookieService.delete('ssolang');
    this.cookieService.delete('vtManagerType');
    this.cookieService.delete('vtUserType');
    this.cookieService.delete('isSsoFederated');
    this.cookieService.delete('vtImpersonatorTokenKey');

    setTimeout(() => {
      this._facilityService.reset();
    }, 3000);
  }

  isUploading() {
    return this._vdoLibraryService.getUploadingVideoList().length !== 0;
  }

  getRootScopeData(): void {
    this.rootScopeService.getRootScope().subscribe((rootScopeData) => (this.rootScopeData = rootScopeData));
  }

  ngOnInit() {
    const path = window.location.href.split('?')[0];
    this.currentPath = path.substring(path.lastIndexOf('/'));
    if (commonVar.paths.includes(this.currentPath)) {
      this.isCommonPath = true;
    } else {
      this.currentHostName = window.location.hostname;
      this.authenticationService.authenticateUser();
      this.initializeLoginChecks();
    }

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        // Navigation Ended Successfully.
        this.currentPathCss = `page${event.url.replace('/', '-')}`;
      }
    });
  }

  ngOnDestroy() {
    this._routeOnChange.unsubscribe();
  }

  redirectBasedOnAuthentication(): void {
    if (this.rootScopeData.isAuthenticated && localStorage.getItem('isLoggedIn') !== 'true') {
      this.router.navigate(['/dashboard']);
      localStorage.setItem('isLoggedIn', 'true');
    }
    if (
      !this.rootScopeData.isAuthenticated &&
      (localStorage.getItem('isLoggedIn') === 'true' || localStorage.getItem('isLoggedIn') === null)
    ) {
      localStorage.setItem('isLoggedIn', 'false');
      this.router.navigate(['/landing']);
    }
  }

  // MOVED from LOGIN COMPONENT
  // loggedIn user redirect to dashboard ,SSO user verification and redirection
  initializeLoginChecks() {
    if (this.cookieService.check('ssolang') === true) {
      this.rootScopeData.selectedLang = this.cookieService.get('ssolang');
    }
    const url = window.location.href;
    if (url.indexOf('ssofedlogin') > -1) {
      if (this.cookieService.get('ssoFederated') === 'true') {
        if (url.indexOf('p_error_codes_list') > -1 || url.indexOf('p_error_code') > -1) {
          this.rootScopeData.selectedLang = this.activatedRoute.snapshot.queryParams['lang'];
          if (url.indexOf('OAM-5') > -1) {
            this.oamError = true;
          }
          this.cookieService.delete('ssoCookie');
          this.cookieService.delete('ssoFederated');
          this.isSSoError = true;
        } else {
          const cookieExists: boolean = this.cookieService.check('ssoCookie');
          if (cookieExists === true) {
            this.ssoCookie = this.cookieService.get('ssoCookie');
            this.syncSsoPassword.post(this.ssoCookie).subscribe(
              (data) => {
                this.getSSOUserDetails(this.ssoCookie);
              },
              (error) => {
                console.error('API failed.');
              }
            );
          }
        }
      }
    } else {
      this.cookieService.delete('ssoCookie');
      this.cookieService.delete('ssoFederated');
    }
  }

  getSSOUserDetails(ssoCookie: string) {
    this.authenticateSsoUsers.post(ssoCookie).subscribe(
      (data) => {
        this.cookieService.set('vtTokenKey', ssoCookie, 0, '', '', true, 'None');
        this.cookieService.delete('ssoCookie');
        const staySigned = this.cookieService.get('staySignedIn');
        if (staySigned === 'true') {
          localStorage.setItem('vtTokenKey', ssoCookie);
        } else {
          sessionStorage.setItem('vtTokenKey', ssoCookie);
        }
        this.cookieService.delete('staySignedIn');
        this.userValidations = data;
        // verify user type of users
        this.verifyUserTypeandRedirectToHome(this.userValidations);
      },
      (error) => {
        console.error('API failed.');
      }
    );
  }

  verifyUserTypeandRedirectToHome(userValidations: any) {
    if (userValidations.gcpToken) localStorage.setItem('gcpToken', userValidations.gcpToken);
    // removal of cookie req after the dev of further pages. Local storage is only req.
    if (userValidations.isSSOFederated !== 'true') {
      if (this.staySigned === true) {
        localStorage.setItem('vtTokenKey', userValidations.tokenKey);
      } else {
        sessionStorage.setItem('vtTokenKey', userValidations.tokenKey);
      }
      this.cookieService.set('vtTokenKey', userValidations.tokenKey, 0, '', '', true, 'None');
    }

    if (typeof userValidations.userType !== 'undefined') {
      localStorage.setItem('vtUserType', userValidations.userType);
      this.cookieService.set('vtUserType', userValidations.userType, 0, '', '', true, 'None');
    }

    if (typeof userValidations.managerType !== 'undefined') {
      localStorage.setItem('vtManagerType', userValidations.managerType);
      this.cookieService.set('vtManagerType', userValidations.managerType, 0, '', '', true, 'None');
    }

    if (userValidations.preferredLanguage !== this.rootScopeData.selectedLang) {
      this.rootScopeData.enablePopup = true;
    } else {
      this.chechkManagerType(userValidations);
    }
  }

  chechkManagerType(userValidations: any) {
    // check manger Fac Type- conencted or Nonconnected
    this.authenticationService.authenticateUser();
  }

  onClick(event: Event) {
    if (this.authenticationService.isAuthenticated()) {
      this._amplitudeService.setUserInteraction();
    }
  }

  isDownloadSoftwareRoute(url: string): boolean {
    const softwareDownloadRoute = ['/softwareDownload'];
    if (!softwareDownloadRoute.includes(url)) {
      return false;
    }
    return true;
  }
}
